import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router';

import { GlassLink } from '@components/glass/GlassButton';
import { GlassSurface } from '@components/glass/GlassSurface';
import { NAV_ITEMS, PRIMARY_CTA } from '@config/site';
import { useCloseMenu, useIsMenuOpen } from '@stores/index';
import { cx } from '@utils/cx';

import { MOBILE_MENU_ID } from './ids';
import styles from './MobileMenu.module.scss';

/**
 * The navigation sheet on narrow screens: a disclosure, not a modal.
 *
 * Escape and a tap outside both close it. On open, focus moves to the first
 * link so keyboard users are not left behind on the toggle. It stays in the
 * DOM while closed so it can animate out; `visibility: hidden` removes it
 * from the tab order and the accessibility tree at the end of the fade.
 */
export const MobileMenu = () => {
  const isOpen = useIsMenuOpen();
  const closeMenu = useCloseMenu();
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    firstLinkRef.current?.focus();

    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, closeMenu]);

  return (
    <div className={styles.root} data-open={isOpen || undefined}>
      <div className={styles.scrim} onClick={closeMenu} aria-hidden="true" />
      <GlassSurface
        as="nav"
        id={MOBILE_MENU_ID}
        variant="thick"
        className={styles.sheet}
        aria-label="Primary"
      >
        <ul className={styles.list} role="list">
          {NAV_ITEMS.map((item, index) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                ref={index === 0 ? firstLinkRef : undefined}
                viewTransition
                className={({ isActive }) => cx(styles.link, isActive && styles.active)}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <GlassLink
          to={PRIMARY_CTA.to}
          tone="primary"
          size="lg"
          icon="arrowRight"
          className={styles.cta}
        >
          {PRIMARY_CTA.label}
        </GlassLink>
      </GlassSurface>
    </div>
  );
};
